import { Link, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import Button from './Button';
import { Cog6ToothIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { PiCrownSimpleFill } from "react-icons/pi";
import { useState, useRef, useEffect } from 'react';
import { useScroll } from '../contexts/ScrollContext';

const HeaderContainer = styled.header`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transform: translateY(${props => props.$isVisible ? '0' : '-100%'});
  transition: transform 0.3s ease;
`;

const HeaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transform: translateY(${props => props.$isVisible ? '0' : '-100%'});
  transition: transform 0.3s ease;
`;

const HeaderContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  font-size: 1.5rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  margin-right: ${({ theme }) => theme.spacing.sm};
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 768px) {
    > *:not(:last-child) {
      display: none;
    }
  }
`;

const NavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const IconLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.7;
  transition: opacity 0.2s;
  display: ${props => props.$isAuthenticated ? 'block' : 'none'};

  &:hover {
    opacity: 1;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  .desktop-icon {
    display: block;
  }
  .mobile-icon {
    display: none;
  }

  @media (max-width: 768px) {
    display: block;
    
    .desktop-icon {
      display: none;
    }
    .mobile-icon {
      display: block;
    }
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const PremiumButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: linear-gradient(
    60deg,
    #ffd700,
    #f5cc00,
    #ffed4a,
    #fff1a6,
    #ffd700
  );
  background-size: 300% 300%;
  animation: ${shimmer} 8s linear infinite;
  border: none;
  color: #8b6b0e;
  font-weight: 600;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  }
  
  svg {
    width: 18px;
    height: 18px;
    color: #8b6b0e;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: ${({ theme }) => theme.spacing.sm};
  min-width: 200px;
  z-index: 1000;
  margin-top: ${({ theme }) => theme.spacing.sm};
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    min-width: unset;
    right: -16px;
  }
`;

const DropdownItem = styled(Link)`
  display: block;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

const DropdownDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const LogoutButton = styled(Button)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.error}20;
  border-color: ${({ theme }) => theme.colors.error};

  &:hover {
    background: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.black};
  }
`;

const SignInButton = styled(Button)`
  font-weight: 500;
`;

const DashboardButton = styled(Button)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileDashboardButton = styled(Button)`
  display: none;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const PremiumBanner = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  display: none;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: linear-gradient(
    60deg,
    #ffd700,
    #f5cc00,
    #ffed4a,
    #fff1a6,
    #ffd700
  );
  background-size: 300% 300%;
  animation: ${shimmer} 8s linear infinite;
  border: none;
  cursor: pointer;
  color: #8b6b0e;
  font-weight: 600;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }

  svg {
    width: 18px;
    height: 18px;
    color: #8b6b0e;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Header = () => {
  const { user, isPremium, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { isVisible } = useScroll();

  const handleLogout = async () => {
    try {
      await logout();
      setIsDropdownOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <HeaderWrapper $isVisible={isVisible}>
      <HeaderContent>
        <Nav>
          <Logo to={user ? '/' : '/'}>
            <LogoImage src="/images/binventory-logo-minimal.png" alt="Binventory Logo" />
            <LogoImage src="/images/BINventory.png" alt="Binventory Logo" />
          </Logo>
          <NavLinks>
            {!user ? (
              <>
                <NavLink to="/pricing">Pricing</NavLink>
                <SignInButton variant="primary" onClick={() => navigate('/login')}>
                  Sign In
                </SignInButton>
                <DropdownContainer ref={dropdownRef}>
                  <IconLink 
                    as="button" 
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
                    title="Menu"
                    $isAuthenticated={false}
                  >
                    <Bars3Icon />
                  </IconLink>
                  <DropdownMenu isOpen={isDropdownOpen}>
                    <DropdownItem to="/pricing" onClick={() => setIsDropdownOpen(false)}>
                      Pricing
                    </DropdownItem>
                    <DropdownDivider />
                    <SignInButton 
                      variant="primary" 
                      onClick={() => {
                        setIsDropdownOpen(false);
                        navigate('/login');
                      }}
                      style={{ width: '100%' }}
                    >
                      Sign In
                    </SignInButton>
                  </DropdownMenu>
                </DropdownContainer>
              </>
            ) : (
              <>
                {!isPremium && (
                  <PremiumButton 
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/pricing');
                    }}
                  >
                    Premium
                    <PiCrownSimpleFill />
                  </PremiumButton>
                )}
                <DashboardButton variant="secondary" onClick={() => navigate('/dashboard')}>
                  Dashboard
                </DashboardButton>
                <DropdownContainer ref={dropdownRef}>
                  <IconLink 
                    as="button" 
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
                    title="Settings"
                    $isAuthenticated={true}
                  >
                    <span className="desktop-icon">
                      <Cog6ToothIcon />
                    </span>
                    <span className="mobile-icon">
                      <Bars3Icon />
                    </span>
                  </IconLink>
                  <DropdownMenu isOpen={isDropdownOpen}>
                    <MobileDashboardButton variant="secondary" onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/dashboard');
                    }}>
                      Dashboard
                    </MobileDashboardButton>
                    <DropdownItem to="/settings" onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/settings');
                    }}>
                      Account Settings
                    </DropdownItem>
                    <DropdownItem to="/usage" onClick={() => {
                      setIsDropdownOpen(false);
                      navigate('/usage');
                    }}>
                      Usage
                    </DropdownItem>
                    <DropdownDivider />
                    <LogoutButton variant="secondary" onClick={handleLogout}>
                      Logout
                    </LogoutButton>
                  </DropdownMenu>
                </DropdownContainer>
              </>
            )}
          </NavLinks>
        </Nav>
      </HeaderContent>
      {user && !isPremium && (
        <PremiumBanner onClick={() => navigate('/pricing')}>
          <PiCrownSimpleFill /> Go Premium
        </PremiumBanner>
      )}
    </HeaderWrapper>
  );
};

export default Header; 